import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  Product,
  ProductCreateInput,
  ProductDetail,
  ProductImportResult,
  ProductListParams,
  ProductUpdateInput,
} from '@/domain/entities/product.entity'
import type { ProductRepository } from '@/domain/repositories/product.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { ImportResultDto, ProductDto } from './product.dto'
import { productMapper } from './product.mapper'

/**
 * PUT /products/:id bersifat partial. Body hanya boleh memuat key yang benar
 * benar disentuh user: key yang absen berarti "jangan ubah", sedangkan
 * mengirim array kosong berarti "lepas semua tautan". Menyalin seluruh objek
 * ke body akan diam-diam menulis ulang data yang tidak dibuka user.
 */
function toUpdateBody(input: ProductUpdateInput): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (input.sku !== undefined) body.sku = input.sku
  if (input.name !== undefined) body.name = input.name
  // BE memakai string kosong untuk melepas kategori.
  if (input.categoryId !== undefined) body.category_id = input.categoryId ?? ''
  if (input.price1 !== undefined) body.price1 = input.price1
  if (input.price2 !== undefined) body.price2 = input.price2
  if (input.price3 !== undefined) body.price3 = input.price3
  if (input.manualHpp !== undefined) body.manual_hpp = input.manualHpp
  if (input.imagePath !== undefined) body.image_path = input.imagePath ?? ''
  if (input.stockActive !== undefined) body.stock_active = input.stockActive
  if (input.stock !== undefined) body.stock = input.stock
  if (input.sourceId !== undefined) body.source_id = input.sourceId ?? ''
  if (input.variantGroupName !== undefined) body.variant_group_name = input.variantGroupName ?? ''
  if (input.variantIds !== undefined) body.variant_ids = input.variantIds
  if (input.addonIds !== undefined) body.addon_ids = input.addonIds
  if (input.addonGroupIds !== undefined) body.addon_group_ids = input.addonGroupIds
  if (input.isPackage !== undefined) body.is_package = input.isPackage
  if (input.packageItems !== undefined) {
    body.package_items = input.packageItems.map((pi) => ({
      product_id: pi.productId,
      qty: pi.qty,
      ord: pi.ord,
    }))
  }
  if (input.packageGroups !== undefined) {
    body.package_groups = input.packageGroups.map((g) => ({
      name: g.name,
      ord: g.ord,
      items: g.items.map((it) => ({
        product_id: it.productId,
        qty: it.qty,
        ord: it.ord,
      })),
    }))
  }
  return body
}

export class ProductHttpRepository implements ProductRepository {
  async list(outletId: string, params: ProductListParams): Promise<Paged<Product>> {
    const raw = await httpClient.get<unknown>('/products', {
      params: {
        outlet_id: outletId,
        category_id: params.categoryId ?? undefined,
        active: params.active ?? undefined,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
      },
    })
    const paged = unwrapPaged<ProductDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(productMapper.toProduct) }
  }

  async get(id: string): Promise<ProductDetail> {
    const raw = await httpClient.get<unknown>(`/products/${id}`)
    return productMapper.toProductDetail(unwrap<ProductDto>(raw))
  }

  async create(outletId: string, input: ProductCreateInput): Promise<Product> {
    const raw = await httpClient.post<unknown>('/products', {
      outlet_id: outletId,
      sku: input.sku,
      name: input.name,
      category_id: input.categoryId ?? '',
      price1: input.price1,
      price2: input.price2,
      price3: input.price3,
      manual_hpp: input.manualHpp ?? null,
      image_path: input.imagePath ?? '',
      stock_active: input.stockActive,
      stock: input.stock,
      source_id: input.sourceId ?? '',
      variant_group_name: input.variantGroupName ?? '',
      variant_ids: input.variantIds,
      addon_ids: input.addonIds,
      addon_group_ids: input.addonGroupIds,
      is_package: input.isPackage,
      package_items: input.packageItems.map((pi) => ({
        product_id: pi.productId,
        qty: pi.qty,
        ord: pi.ord,
      })),
      package_groups: (input.packageGroups ?? []).map((g) => ({
        name: g.name,
        ord: g.ord,
        items: g.items.map((it) => ({
          product_id: it.productId,
          qty: it.qty,
          ord: it.ord,
        })),
      })),
    })
    return productMapper.toProduct(unwrap<ProductDto>(raw))
  }

  async update(id: string, input: ProductUpdateInput): Promise<Product> {
    const raw = await httpClient.put<unknown>(`/products/${id}`, toUpdateBody(input))
    return productMapper.toProduct(unwrap<ProductDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/products/${id}`)
  }

  async setActive(id: string, active: boolean): Promise<void> {
    await httpClient.put(`/products/${id}/active`, { active })
  }

  async importXlsx(outletId: string, file: File): Promise<ProductImportResult> {
    const form = new FormData()
    form.append('outlet_id', outletId)
    form.append('file', file)
    const raw = await httpClient.post<unknown>('/products/import', form)
    return productMapper.toImportResult(unwrap<ImportResultDto>(raw))
  }

  async exportXlsx(outletId: string): Promise<{ blob: Blob; filename?: string }> {
    return httpClient.download('/products/export', { params: { outlet_id: outletId } })
  }
}
